import { scrollElementAnimated } from './animations';

const ANIMATION_DURATION = 250;
export const rootLevelMenuItemSelector = '[data-item-depth="0"]';
export const scrollPageButtonSelector = '[data-menu-scroll-action="page"]';

export const scrollToMenuItem = (
  containerNode: HTMLElement,
  menuItem: HTMLElement,
) => {
  const menuItemButton = menuItem.firstChild;

  const { left: navStartLeft } = containerNode.getBoundingClientRect();

  const navComputedStyles = getComputedStyle(containerNode);

  // get Menu's viewport's width
  const navWidth =
    containerNode.clientWidth -
    parseFloat(navComputedStyles.paddingLeft) -
    parseFloat(navComputedStyles.paddingRight);

  // get Menu's viewport's left offset
  const navLeft =
    navStartLeft +
    containerNode.clientLeft +
    parseFloat(navComputedStyles.paddingLeft);

  const {
    width: itemWidth,
    left: itemLeft,
  } = (menuItemButton as Element)?.getBoundingClientRect();

  const isRightOverflow = itemLeft + itemWidth > navWidth + navLeft;
  const isLeftOverflow = itemLeft < navLeft;

  // make sure the element is not already completely visible inside Menu's viewport
  const isMenuItemOverflows = isRightOverflow || isLeftOverflow;

  if (isMenuItemOverflows) {
    const scrollButton = containerNode.querySelector(scrollPageButtonSelector);

    const { width: scrollButtonWidth } = scrollButton?.getBoundingClientRect()!;

    const moveTo =
      containerNode.scrollLeft +
      Math.min(
        itemLeft - navLeft - scrollButtonWidth,
        containerNode.scrollWidth - navWidth + scrollButtonWidth,
      );

    return scrollElementAnimated({
      element: containerNode,
      moveTo,
      duration: ANIMATION_DURATION,
    });
  }
};

const getFirstVisibleItemIndex = (
  menuItemNodes: Array<HTMLElement>,
  menuLeft: number,
  menuWidth: number,
) => {
  return menuItemNodes.findIndex(item => {
    const { left: itemLeft } = item.getBoundingClientRect();
    /**
     * item must be inside menu's viewport:
     * item's left border is positioned to the right of menu's left border,
     * but not farther than menu's right border
     *
     * item must be leftmost inside menu's viewport:
     * we take first matching the viewport condition
     */
    return itemLeft >= menuLeft && itemLeft <= menuLeft + menuWidth;
  });
};

const getLastVisibleItemIndex = (
  menuItemsNodes: Array<HTMLElement>,
  menuLeft: number,
  menuWidth: number,
) => {
  const lastVisibleItemIndex = [...menuItemsNodes].reverse().findIndex(item => {
    const { left: itemLeft } = item.getBoundingClientRect();
    /**
     * item is inside menu's viewport:
     * item's LEFT border is to the left of menu's RIGHT border
     *
     * item is rightmost in menu's viewport:
     * since we reversed items array, the first matching the viewport condition
     * is actually last visible inside viewport
     */
    return itemLeft <= menuLeft + menuWidth;
  });

  return menuItemsNodes.length - 1 - lastVisibleItemIndex;
};

const getDOMMenuItems = (
  menuNode: HTMLElement,
  isRTL: boolean,
): Array<HTMLElement> => {
  if (!menuNode) {
    return [];
  }

  const menuItems = Array.from(
    menuNode.querySelectorAll<HTMLElement>(rootLevelMenuItemSelector),
  );

  return isRTL ? [...menuItems].reverse() : menuItems;
};

export const scrollPageToTheLeft = (menuNode: HTMLElement, isRTL: boolean) => {
  const {
    left: menuContainerLeft,
    width: menuContainerWidth,
  } = menuNode.getBoundingClientRect();

  const menuItems = getDOMMenuItems(menuNode, isRTL);

  const firstVisibleItemIndex = getFirstVisibleItemIndex(
    menuItems,
    menuContainerLeft,
    menuContainerWidth,
  );
  /**
   * looking for menu item we need to scroll to,
   * so that menu viewport is filled with new items
   *
   */
  let targetMenuItem;

  for (let index = firstVisibleItemIndex - 1; index >= 0; index--) {
    targetMenuItem = menuItems[index];
    const { left: itemLeft } = targetMenuItem.getBoundingClientRect();
    /**
     * menuContainerLeft - itemLeft is the distance from item's left border to menu's left border,
     * if it is bigger than menu width, than we are far enough to have a full page and can stop
     *
     */
    if (menuContainerLeft - itemLeft > menuContainerWidth) {
      if (index !== firstVisibleItemIndex - 1) {
        targetMenuItem = menuItems[index + 1];
      }
      break;
    }
  }
  if (targetMenuItem) {
    scrollToMenuItem(menuNode, targetMenuItem);
  }
};

export const scrollPageToTheRight = (menuNode: HTMLElement, isRTL: boolean) => {
  const menuItems = getDOMMenuItems(menuNode, isRTL);

  const {
    left: menuContainerLeft,
    width: menuContainerWidth,
  } = menuNode.getBoundingClientRect();
  const lastVisibleItemIndex = getLastVisibleItemIndex(
    menuItems,
    menuContainerLeft,
    menuContainerWidth,
  );
  const menuItem = menuItems[lastVisibleItemIndex];

  const {
    left: menuItemLeft,
    width: menuItemWidth,
  } = menuItem.getBoundingClientRect();

  if (menuItemLeft + menuItemWidth <= menuContainerLeft + menuContainerWidth) {
    const index = Math.min(menuItems.length - 1, lastVisibleItemIndex + 1);
    scrollToMenuItem(menuNode, menuItems[index]);
  } else {
    scrollToMenuItem(menuNode, menuItem);
  }
};
